import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Permission, StudentFafsa, StudentFafsaEditResource, StudentFafsaViewResource } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentFafsaPageBody } from "./StudentFafsaPageBody";
import { StudentFafsaPageHeader } from "./StudentFafsaPageHeader";

const routeName = "studentFafsa";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  studentId?: number = 0;
}

export const studentFafsaPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "FAFSA",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const StudentFafsaPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentFafsa;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [fafsa, setFafsa] = useState<StudentFafsa>({} as StudentFafsa);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [fafsa] });
  const [viewResource, setViewResource] = useState<StudentFafsaViewResource>({});
  const [editResource, setEditResource] = useState<StudentFafsaEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${studentFafsaPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`);

  const fetch = (id: number) => {
    ApiService.studentFafsas.get(id).then((response) => {
      if (id === 0) {
        if (optionalParams.studentId) response.studentId = optionalParams.studentId;
      }

      setFafsa(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.studentFafsas.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentFafsas.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StudentFafsaPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentFafsaPageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      fafsa={fafsa}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentFafsaPageBody
      crudMode={params.crudMode}
      fafsa={fafsa}
      setFafsa={setFafsa}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
